/**
 * Merchant Gateway Management — per-merchant Paystack/Flutterwave state.
 *
 * Distinct from PaymentGatewayManagement (which manages the platform-level
 * gateway records + credentials). This surface manages which gateways each
 * MERCHANT has enabled on their store, and cleans up the stores that ended
 * up with empty `enabledGateways` (root-cause fix is in storesPrisma.js).
 *
 * Backed by /api/admin/payment-gateways/{summary,stores,bulk-update,fix-empty}.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Search, Loader2, Check, X, Wand2 } from 'lucide-react';

const HAIRLINE = '#EEF2F0';
const INK      = '#111827';
const MUTED    = '#6B7280';
const BRAND    = '#0d7463';
const DANGER   = '#B45309';

const STATUS_OPTIONS = [
  { value: 'all',              label: 'All stores' },
  { value: 'none',             label: 'No gateways' },
  { value: 'paystack_only',    label: 'Paystack only' },
  { value: 'flutterwave_only', label: 'Flutterwave only' },
  { value: 'both',             label: 'Both enabled' },
];

const merchantDisplay = (store) => {
  if (store.name) return store.name;
  const o = store.owner || {};
  const full = [o.firstName, o.lastName].filter(Boolean).join(' ').trim();
  return full || o.email || (o.merchantCode ? `Code: ${o.merchantCode}` : '—');
};

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' })
  : '—';

const pct = (n, d) => d > 0 ? `${Math.round((n / d) * 100)}%` : '0%';

const MerchantGatewayManagement = () => {
  const [summary, setSummary]     = useState(null);
  const [stores, setStores]       = useState([]);
  const [pagination, setPagination] = useState({ total: 0, offset: 0, limit: 50, hasMore: false });
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [status, setStatus]       = useState('all');
  const [search, setSearch]       = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selected, setSelected]   = useState(new Set());
  const [busy, setBusy]           = useState(false);
  const [banner, setBanner]       = useState('');

  const authOpts = () => ({ headers: { 'Content-Type': 'application/json' }, credentials: 'include' });

  const loadSummary = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/payment-gateways/summary', authOpts());
      const d = await r.json();
      if (d.success) setSummary(d);
    } catch (e) {
      console.error('summary load failed', e);
    }
  }, []);

  const loadStores = useCallback(async (offset = 0) => {
    try {
      setLoading(true);
      setError('');
      const qs = new URLSearchParams({
        offset: String(offset),
        limit:  String(pagination.limit),
        status,
        ...(search ? { search } : {}),
      });
      const r = await fetch(`/api/admin/payment-gateways/stores?${qs}`, authOpts());
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Failed to load stores');
      setStores(d.stores);
      setPagination(d.pagination);
      setSelected(new Set());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [status, search, pagination.limit]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadStores(0); }, [loadStores]);

  const flashBanner = (msg) => {
    setBanner(msg);
    setTimeout(() => setBanner(''), 4000);
  };

  const toggleGateway = async (storeId, gateway, enable) => {
    setBusy(true);
    try {
      const r = await fetch('/api/admin/payment-gateways/bulk-update', {
        ...authOpts(),
        method: 'POST',
        body: JSON.stringify({
          storeIds: [storeId],
          gateways: [gateway],
          action: enable ? 'enable' : 'disable',
        }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Update failed');
      await Promise.all([loadSummary(), loadStores(pagination.offset)]);
      flashBanner(`${gateway} ${enable ? 'enabled' : 'disabled'} for 1 store`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const runBulk = async (gateways, action) => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const r = await fetch('/api/admin/payment-gateways/bulk-update', {
        ...authOpts(),
        method: 'POST',
        body: JSON.stringify({
          storeIds: Array.from(selected),
          gateways,
          action,
        }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Bulk update failed');
      await Promise.all([loadSummary(), loadStores(pagination.offset)]);
      flashBanner(`${action === 'enable' ? 'Enabled' : 'Disabled'} ${gateways.join(' + ')} for ${d.updated} stores`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const runFixEmpty = async () => {
    if (!window.confirm('Enable Paystack + Flutterwave for every store with no gateways currently enabled?')) return;
    setBusy(true);
    try {
      const r = await fetch('/api/admin/payment-gateways/fix-empty', {
        ...authOpts(),
        method: 'POST',
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Fix failed');
      await Promise.all([loadSummary(), loadStores(pagination.offset)]);
      flashBanner(`Fixed ${d.fixed} stores (scanned ${d.scanned})`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleSelectAll = () => {
    if (selected.size === stores.length) setSelected(new Set());
    else setSelected(new Set(stores.map((s) => s.id)));
  };

  const allSelected = stores.length > 0 && selected.size === stores.length;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Merchant Gateway Management</h1>
          <p style={styles.subtitle}>Enable or disable Paystack / Flutterwave per merchant. Bulk actions available.</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.iconBtn} onClick={() => { loadSummary(); loadStores(pagination.offset); }} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div style={styles.summarySection}>
          <div style={styles.summaryGroup}>
            <div style={styles.summaryGroupHead}>Coverage</div>
            <div style={styles.summaryRow}>
              <SummaryCard label="Total stores"       amount={summary.total}       strong />
              <SummaryCard label="Both enabled"       amount={summary.both}        sub={pct(summary.both, summary.total)} />
              <SummaryCard label="Paystack only"      amount={summary.paystackOnly} sub={pct(summary.paystackOnly, summary.total)} />
              <SummaryCard label="Flutterwave only"   amount={summary.flutterwaveOnly} sub={pct(summary.flutterwaveOnly, summary.total)} />
            </div>
          </div>
          <div style={styles.summaryGroup}>
            <div style={styles.summaryGroupHead}>Stuck</div>
            <div style={styles.summaryRow}>
              <SummaryCard
                label="No gateways enabled"
                amount={summary.none}
                sub={`${pct(summary.none, summary.total)} — customers can't check out`}
                tone="warn"
              />
            </div>
          </div>
        </div>
      )}

      {/* Fix-empty widget (only if stuck stores exist) */}
      {summary?.none > 0 && (
        <div style={styles.fixCard}>
          <div>
            <div style={styles.fixTitle}>One-click fix</div>
            <div style={styles.fixSub}>
              Enable Paystack + Flutterwave for the {summary.none} store{summary.none === 1 ? '' : 's'} with no gateways currently enabled. Idempotent — safe to run repeatedly.
            </div>
          </div>
          <button style={styles.primaryBtn} onClick={runFixEmpty} disabled={busy}>
            {busy ? <><Loader2 size={14} className="spin" /> Working…</> : <><Wand2 size={14} /> Fix {summary.none} store{summary.none === 1 ? '' : 's'}</>}
          </button>
        </div>
      )}

      {/* Filter/search bar */}
      <div style={styles.filtersBar}>
        <select style={styles.filterInput} value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <form onSubmit={onSearchSubmit} style={styles.searchForm}>
          <Search size={14} color={MUTED} />
          <input
            style={styles.searchInput}
            type="text"
            placeholder="Search store name, merchant email or code…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {search && (
            <button
              type="button"
              style={styles.clearBtn}
              onClick={() => { setSearchInput(''); setSearch(''); }}
            >Clear</button>
          )}
        </form>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div style={styles.bulkBar}>
          <div style={styles.bulkCount}>{selected.size} selected</div>
          <div style={styles.bulkActions}>
            <span style={styles.bulkLabel}>Enable:</span>
            <button style={styles.bulkBtn} onClick={() => runBulk(['paystack'], 'enable')}      disabled={busy}>Paystack</button>
            <button style={styles.bulkBtn} onClick={() => runBulk(['flutterwave'], 'enable')}   disabled={busy}>Flutterwave</button>
            <button style={styles.bulkBtn} onClick={() => runBulk(['paystack', 'flutterwave'], 'enable')} disabled={busy}>Both</button>
            <span style={styles.bulkSep}>·</span>
            <span style={styles.bulkLabel}>Disable:</span>
            <button style={styles.bulkBtnGhost} onClick={() => runBulk(['paystack'], 'disable')}    disabled={busy}>Paystack</button>
            <button style={styles.bulkBtnGhost} onClick={() => runBulk(['flutterwave'], 'disable')} disabled={busy}>Flutterwave</button>
            <button style={styles.bulkBtnGhost} onClick={() => runBulk(['paystack', 'flutterwave'], 'disable')} disabled={busy}>Both</button>
          </div>
        </div>
      )}

      {banner && <div style={styles.successBanner}>{banner}</div>}
      {error && <div style={styles.errorBanner}>{error}</div>}

      {/* Table */}
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: 40 }}>
                <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
              </th>
              <th style={styles.th}>Merchant / Store</th>
              <th style={styles.th}>Tier</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Paystack</th>
              <th style={{ ...styles.th, textAlign: 'center' }}>Flutterwave</th>
              <th style={styles.th}>Last updated</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} style={styles.loading}><Loader2 size={16} className="spin" /> Loading…</td></tr>
            )}
            {!loading && stores.length === 0 && (
              <tr><td colSpan={6} style={styles.empty}>No stores match the current filter.</td></tr>
            )}
            {!loading && stores.map((s) => (
              <tr key={s.id} style={styles.tr}>
                <td style={styles.td}>
                  <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggleSelect(s.id)} />
                </td>
                <td style={styles.td}>
                  <div style={styles.merchantName}>{merchantDisplay(s)}</div>
                  <div style={styles.merchantSub}>
                    {s.owner?.email || '—'}
                    {s.owner?.merchantCode ? ` · ${s.owner.merchantCode}` : ''}
                  </div>
                </td>
                <td style={styles.td}>
                  <span style={styles.tierPill}>{s.owner?.merchantTier || 'FREE'}</span>
                </td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <GatewayToggle
                    on={s.paystackEnabled}
                    onClick={() => toggleGateway(s.id, 'paystack', !s.paystackEnabled)}
                    disabled={busy}
                  />
                </td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <GatewayToggle
                    on={s.flutterwaveEnabled}
                    onClick={() => toggleGateway(s.id, 'flutterwave', !s.flutterwaveEnabled)}
                    disabled={busy}
                  />
                </td>
                <td style={{ ...styles.td, color: MUTED, fontSize: 12 }}>{fmtDate(s.updatedAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {!loading && pagination.total > pagination.limit && (
          <div style={styles.pager}>
            <span style={styles.pagerInfo}>
              {pagination.offset + 1}–{Math.min(pagination.offset + stores.length, pagination.total)} of {pagination.total}
            </span>
            <div style={styles.pagerBtns}>
              <button
                style={styles.pagerBtn}
                onClick={() => loadStores(Math.max(0, pagination.offset - pagination.limit))}
                disabled={pagination.offset === 0}
              >
                Prev
              </button>
              <button
                style={styles.pagerBtn}
                onClick={() => loadStores(pagination.offset + pagination.limit)}
                disabled={!pagination.hasMore}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ label, amount, sub, tone, strong }) => (
  <div style={styles.card}>
    <div style={styles.cardLabel}>{label}</div>
    <div style={{
      ...styles.cardAmount,
      color: tone === 'warn' ? DANGER : INK,
      fontWeight: strong ? 700 : 500,
    }}>
      {(Number(amount) || 0).toLocaleString('en-NG')}
    </div>
    {sub && <div style={styles.cardSub}>{sub}</div>}
  </div>
);

const GatewayToggle = ({ on, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      ...styles.togglePill,
      backgroundColor: on ? '#ECFDF5' : '#FFFFFF',
      borderColor:     on ? '#A7F3D0' : HAIRLINE,
      color:           on ? '#065F46' : MUTED,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
    }}
    title={on ? 'Enabled — click to disable' : 'Disabled — click to enable'}
  >
    {on ? <Check size={14} /> : <X size={14} />}
    {on ? 'Enabled' : 'Disabled'}
  </button>
);

const styles = {
  page: { padding: 24, maxWidth: 1400, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: 600, color: INK, margin: 0 },
  subtitle: { fontSize: 13, color: MUTED, margin: '4px 0 0' },
  headerActions: { display: 'flex', gap: 8 },
  iconBtn: {
    width: 36, height: 36, borderRadius: 8,
    border: '1px solid ' + HAIRLINE, backgroundColor: '#FFFFFF',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: INK,
  },

  summarySection: { display: 'flex', flexWrap: 'wrap', gap: 24, marginBottom: 20 },
  summaryGroup: { flex: 1, minWidth: 340 },
  summaryGroupHead: {
    fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em',
    fontWeight: 600, marginBottom: 8,
  },
  summaryRow: {
    display: 'flex', gap: 1,
    backgroundColor: HAIRLINE, border: '1px solid ' + HAIRLINE,
    borderRadius: 10, overflow: 'hidden',
  },
  card: { flex: 1, minWidth: 0, padding: '14px 16px', backgroundColor: '#FFFFFF' },
  cardLabel: {
    fontSize: 11, color: MUTED, textTransform: 'uppercase',
    letterSpacing: '0.04em', fontWeight: 600, marginBottom: 6,
  },
  cardAmount: { fontSize: 22, lineHeight: '28px', letterSpacing: '-0.01em' },
  cardSub: { fontSize: 11, color: MUTED, marginTop: 4 },

  fixCard: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: 14, borderRadius: 10, backgroundColor: '#FFFBEB',
    border: '1px solid #FDE68A', marginBottom: 20, gap: 16, flexWrap: 'wrap',
  },
  fixTitle: { fontSize: 14, fontWeight: 600, color: INK },
  fixSub: { fontSize: 12, color: MUTED, marginTop: 2, maxWidth: 600 },

  filtersBar: {
    display: 'flex', flexWrap: 'wrap', gap: 8, padding: 12,
    backgroundColor: '#FFFFFF', border: '1px solid ' + HAIRLINE,
    borderRadius: 10, marginBottom: 16, alignItems: 'center',
  },
  filterInput: {
    padding: '6px 10px', borderRadius: 6,
    border: '1px solid ' + HAIRLINE, fontSize: 13,
    color: INK, backgroundColor: '#FFFFFF',
  },
  searchForm: {
    display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 260,
    padding: '4px 10px', backgroundColor: '#FFFFFF',
    border: '1px solid ' + HAIRLINE, borderRadius: 6,
  },
  searchInput: { border: 'none', outline: 'none', fontSize: 13, flex: 1, backgroundColor: 'transparent', color: INK },
  clearBtn: {
    padding: '4px 8px', border: 'none', backgroundColor: 'transparent',
    color: MUTED, fontSize: 12, cursor: 'pointer',
  },

  bulkBar: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', borderRadius: 10,
    backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0',
    marginBottom: 12, flexWrap: 'wrap', gap: 12,
  },
  bulkCount: { fontSize: 13, fontWeight: 600, color: INK },
  bulkActions: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  bulkLabel: { fontSize: 12, color: MUTED, marginRight: 4 },
  bulkBtn: {
    padding: '5px 10px', borderRadius: 6,
    border: '1px solid ' + BRAND, backgroundColor: BRAND, color: '#FFFFFF',
    fontSize: 12, fontWeight: 500, cursor: 'pointer',
  },
  bulkBtnGhost: {
    padding: '5px 10px', borderRadius: 6,
    border: '1px solid ' + HAIRLINE, backgroundColor: '#FFFFFF', color: INK,
    fontSize: 12, fontWeight: 500, cursor: 'pointer',
  },
  bulkSep: { color: HAIRLINE, margin: '0 4px' },

  primaryBtn: {
    padding: '8px 14px', borderRadius: 8, border: 'none',
    backgroundColor: BRAND, color: '#FFFFFF', fontSize: 13,
    fontWeight: 600, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 6,
  },

  successBanner: {
    padding: '10px 14px', borderRadius: 8,
    backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0',
    color: '#065F46', fontSize: 13, marginBottom: 12,
  },
  errorBanner: {
    padding: '10px 14px', borderRadius: 8,
    backgroundColor: '#FEF2F2', border: '1px solid #FECACA',
    color: '#991B1B', fontSize: 13, marginBottom: 12,
  },

  tableWrap: {
    backgroundColor: '#FFFFFF', border: '1px solid ' + HAIRLINE,
    borderRadius: 10, overflow: 'hidden',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    padding: '10px 14px', textAlign: 'left', fontSize: 11,
    fontWeight: 600, color: MUTED, textTransform: 'uppercase',
    letterSpacing: '0.04em', borderBottom: '1px solid ' + HAIRLINE,
    backgroundColor: '#F9FAFB',
  },
  tr: { borderBottom: '1px solid ' + HAIRLINE },
  td: { padding: '12px 14px', fontSize: 13, color: INK, verticalAlign: 'middle' },
  merchantName: { fontSize: 13, fontWeight: 500, color: INK },
  merchantSub: { fontSize: 11, color: MUTED, marginTop: 2 },
  tierPill: {
    fontSize: 10, fontWeight: 600, padding: '2px 8px',
    borderRadius: 999, backgroundColor: '#F3F4F6', color: MUTED,
    textTransform: 'uppercase', letterSpacing: '0.04em',
  },
  togglePill: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 10px', borderRadius: 999,
    border: '1px solid', fontSize: 12, fontWeight: 500,
  },

  loading: { padding: 40, textAlign: 'center', color: MUTED },
  empty: { padding: 40, textAlign: 'center', color: MUTED, fontSize: 13 },

  pager: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '10px 14px', borderTop: '1px solid ' + HAIRLINE,
  },
  pagerInfo: { fontSize: 12, color: MUTED },
  pagerBtns: { display: 'flex', gap: 8 },
  pagerBtn: {
    padding: '6px 12px', borderRadius: 6,
    border: '1px solid ' + HAIRLINE, backgroundColor: '#FFFFFF',
    fontSize: 12, cursor: 'pointer', color: INK,
  },
};

export default MerchantGatewayManagement;
